import React, { Component } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import NoEmployerError from './NoEmployerError'
import DocumentNotFound from './DocumentNotFound'
import InvalidRestDay from './InvalidRestDay'
import { isDocumentNotFound } from '../services/documentNotFound'
import { isInvalidRestDay } from '../services/invalidRestDay'
import { clearDataCaches } from '../services/dataCaches'
import { buildErrorReport, copyToClipboard } from '../services/errorReport'
import { isStaleBuildError, reloadForStaleBuild } from '../services/staleBuild'

const textCopy = 'העתקת פרטי השגיאה'
const textCopied = 'הפרטים הועתקו — אפשר להדביק בהודעה'
const textCopyFailed = 'ההעתקה נכשלה — אפשר לסמן ולהעתיק ידנית'
const textCopyHint = 'כדי שנוכל לטפל, העתיקו את פרטי השגיאה ושלחו אותם אלינו.'

/**
 * Error Boundary component to catch and handle errors in child components
 * Specifically handles employer-related errors with a friendly UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      at: null,
      copyStatus: null,
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, at: new Date().toISOString() }
  }

  componentDidCatch(error, errorInfo) {
    // Log error information
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo })

    // A tab open across a deploy asks for a chunk the new release deleted. Get
    // the current build instead of showing the user a crash they cannot act on.
    if (isStaleBuildError(error)) {
      reloadForStaleBuild()
    }

    // You could also log to an error reporting service here
    // logErrorToService(error, errorInfo)
  }

  handleReset = () => {
    // #253: re-rendering alone was never a retry. Every read is served from a
    // suspense cache, so the same answer — including a cached "this document
    // does not exist" — came straight back, and the screen could not be
    // recovered from without a full page reload. Forget them first.
    clearDataCaches()
    this.setState({ hasError: false, error: null, errorInfo: null, at: null, copyStatus: null })
  }

  // The console output is captured from app start (services/errorReport), so
  // the copied block holds what happened BEFORE the crash too — that context is
  // what a client report otherwise lacks entirely.
  handleCopy = async () => {
    const { error, errorInfo, at } = this.state
    const copied = await copyToClipboard(buildErrorReport({ error, errorInfo, at }))
    this.setState({ copyStatus: copied ? 'copied' : 'failed' })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      const { error } = this.state
      
      // #253: the document simply is not there. That is an answer, not a
      // fault, and it must be said before the generic crash screen gets it.
      if (isDocumentNotFound(error)) {
        // handleReset forgets the cached reads, so this is a real re-check and
        // not a re-render into the same answer.
        return <DocumentNotFound subject={error.subject} onRetry={this.handleReset} />
      }

      // #542: the engine refused a rest day it cannot read (#505). That is a
      // deliberate refusal about one field on one employee card, not an
      // unexpected error, and it has an action — which the screen below has
      // none of. Screens that know the employee catch it themselves; this is
      // the net for the ones that do not.
      if (isInvalidRestDay(error)) {
        return <InvalidRestDay error={error}/>
      }

      // Check if this is a "no employer" error
      if (error?.message?.includes('מעסיק')) {
        return <NoEmployerError message={error.message} />
      }

      // An outdated tab, not a crash. componentDidCatch already asked for a
      // reload; this is what the user sees while it happens, and what stays on
      // screen if the reload was suppressed to avoid a refresh loop.
      if (isStaleBuildError(error)) {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              padding: 3,
            }}
          >
            <Paper elevation={3} sx={{ padding: 4, textAlign: 'center', maxWidth: 500 }}>
              <Typography variant="h5" gutterBottom data-testid="stale-build-notice">
                גרסה חדשה של המערכת פורסמה
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                הדף נטען מחדש כדי לעבור לגרסה העדכנית. אם לא קרה דבר, לחצו על רענון.
              </Typography>
              <Button
                variant="contained"
                data-testid="stale-build-reload"
                onClick={() => window.location.reload()}
              >
                רענון
              </Button>
            </Paper>
          </Box>
        )
      }

      // Generic error fallback
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            padding: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              textAlign: 'center',
              maxWidth: 500,
            }}
          >
            <ErrorOutlineIcon 
              color="error" 
              sx={{ fontSize: 64, marginBottom: 2 }} 
            />
            <Typography variant="h5" gutterBottom color="error">
              אופס! משהו השתבש
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              אירעה שגיאה בלתי צפויה. אנא נסה שוב או חזור לדף הבית.
            </Typography>
            {/* The message itself, so even a screenshot carries something. */}
            <Typography
              variant="caption"
              component="div"
              dir="ltr"
              data-testid="error-boundary-message"
              sx={{
                backgroundColor: 'grey.100',
                borderRadius: 1,
                padding: 1,
                marginBottom: 2,
                fontFamily: 'monospace',
                textAlign: 'left',
                wordBreak: 'break-word',
              }}
            >
              {String(error?.message || error || '')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {textCopyHint}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ContentCopyIcon/>}
              sx={{ '& .MuiButton-startIcon': { marginRight: '-2px', marginLeft: '8px' } }}
              onClick={this.handleCopy}
              data-testid="error-boundary-copy"
            >
              {textCopy}
            </Button>
            {this.state.copyStatus && (
              <Typography
                variant="body2"
                sx={{ marginTop: 1 }}
                color={this.state.copyStatus === 'copied' ? 'success.main' : 'error'}
                data-testid="error-boundary-copy-status"
              >
                {this.state.copyStatus === 'copied' ? textCopied : textCopyFailed}
              </Typography>
            )}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  marginTop: 2,
                  padding: 2,
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <Typography variant="caption" component="pre" sx={{ margin: 0 }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}
            <Box sx={{ marginTop: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                onClick={this.handleGoHome}
              >
                חזרה לדף הבית
              </Button>
              <Button 
                variant="outlined" 
                onClick={this.handleReset}
              >
                נסה שוב
              </Button>
            </Box>
          </Paper>
        </Box>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
